import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { AdminPanelService } from '../../admin-panel.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

interface EligibleUser {
  id: number;
  username: string;
  email: string;
  organisation: string;
  portfolios: UserPortfolio[];
  portfolio_count: number;
}

interface UserPortfolio {
  id: number;
  type: 'watchlist' | 'benchmark';
  name: string;
  company_count: number;
  subscribed: boolean;
}

interface FilteredUser {
  id: number;
  username: string;
  reason: string;
}

interface Benchmark {
  id: number;
  type: 'benchmark';
  name: string;
  company_count: number;
  selected: boolean;
}

@Component({
  selector: 'app-email-composer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTabsModule
  ],
  templateUrl: './email-composer.component.html',
  styleUrl: './email-composer.component.css'
})
export class EmailComposerComponent implements OnInit {

  emailForm!: FormGroup;
  loading: boolean = false;
  regenerating: boolean = false;
  sending: boolean = false;
  previewHtml: SafeHtml = '';

  eligibleUsers: EligibleUser[] = [];
  filteredOutUsers: FilteredUser[] = [];

  // Portfolio selection options
  includeWatchlists: boolean = true;
  availableBenchmarks: Benchmark[] = [];

  previewNote: string = '';

  constructor(
    private adminPanelService: AdminPanelService,
    private sanitizer: DomSanitizer,
    @Inject(MAT_DIALOG_DATA) public data: { userIds: number[] },
    public dialogRef: MatDialogRef<EmailComposerComponent>
  ) {}

  ngOnInit(): void {
    this.emailForm = new FormGroup({
      subject: new FormControl('', Validators.required)
    });

    this.loadPreview();
  }

  loadPreview() {
    this.loading = true;
    const selectedBenchmarkIds = this.getSelectedBenchmarkIds();

    this.adminPanelService.previewNotification(
      this.data.userIds,
      this.includeWatchlists,
      selectedBenchmarkIds
    ).subscribe({
      next: (res) => {
        this.loading = false;
        this.eligibleUsers = res.payload.eligible_users || [];
        this.filteredOutUsers = res.payload.filtered_out || [];

        // Only set available benchmarks on first load
        if (this.availableBenchmarks.length === 0) {
          this.availableBenchmarks = (res.payload.available_benchmarks || []).map((b: any) => ({
            id: b.id,
            type: 'benchmark',
            name: b.name,
            company_count: b.company_count || 0,
            selected: true  // All selected by default
          }));
        }

        // Set default values
        this.emailForm.get('subject')?.setValue(res.payload.default_subject || 'Portfolio Update - Quant IP');
        this.previewNote = res.payload.preview_note || '';

        this.updatePreview(res.payload.default_body || '');
      },
      error: (err) => {
        this.loading = false;
        alert('Failed to load preview. Please try again.');
        this.dialogRef.close();
      }
    });
  }

  updatePreview(html: string) {
    this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(html);
  }

  onSend() {
    if (this.emailForm.invalid) {
      alert('Please fill in all required fields.');
      return;
    }

    const usersWithPortfolios = this.eligibleUsers.filter(u => u.portfolio_count > 0);
    if (usersWithPortfolios.length === 0) {
      alert('No users have portfolios to send. Select benchmarks or enable watchlists.');
      return;
    }

    const confirmMessage = `Are you sure you want to send personalized emails to ${usersWithPortfolios.length} user(s)?\n\nEach user will receive their own email with only their subscribed portfolios.`;
    if (!confirm(confirmMessage)) {
      return;
    }

    this.sending = true;
    const userIds = this.eligibleUsers.map(u => u.id);
    const subject = this.emailForm.get('subject')?.value;
    const selectedBenchmarkIds = this.getSelectedBenchmarkIds();

    this.adminPanelService.sendNotification(
      userIds,
      subject,
      this.includeWatchlists,
      selectedBenchmarkIds
    ).subscribe({
      next: (res) => {
        this.sending = false;
        const skippedCount = res.payload.skipped_no_portfolios?.length || 0;
        let message = `Successfully sent ${res.payload.sent_count} personalized emails.`;
        if (skippedCount > 0) {
          message += `\n${skippedCount} user(s) skipped (no subscribed portfolios).`;
        }
        this.dialogRef.close({
          sent: true,
          sentCount: res.payload.sent_count,
          failed: res.payload.failed,
          skipped: res.payload.skipped_no_portfolios
        });
      },
      error: (err) => {
        this.sending = false;
        alert('Failed to send notifications. Please try again.');
      }
    });
  }

  onCancel() {
    this.dialogRef.close({ sent: false });
  }

  // Benchmark selection methods
  toggleBenchmark(benchmark: Benchmark) {
    benchmark.selected = !benchmark.selected;
  }

  selectAllBenchmarks() {
    this.availableBenchmarks.forEach(b => b.selected = true);
  }

  deselectAllBenchmarks() {
    this.availableBenchmarks.forEach(b => b.selected = false);
  }

  getSelectedBenchmarkIds(): number[] {
    return this.availableBenchmarks.filter(b => b.selected).map(b => b.id);
  }

  getSelectedBenchmarkCount(): number {
    return this.availableBenchmarks.filter(b => b.selected).length;
  }

  // Regenerate preview with current selection
  regeneratePreview() {
    this.regenerating = true;
    const selectedBenchmarkIds = this.getSelectedBenchmarkIds();

    this.adminPanelService.previewNotification(
      this.data.userIds,
      this.includeWatchlists,
      selectedBenchmarkIds
    ).subscribe({
      next: (res) => {
        this.regenerating = false;
        this.eligibleUsers = res.payload.eligible_users || [];
        this.previewNote = res.payload.preview_note || '';
        this.updatePreview(res.payload.default_body || '');
      },
      error: (err) => {
        this.regenerating = false;
        alert('Failed to regenerate preview. Please try again.');
      }
    });
  }

  // Get count of users who will actually receive emails
  getUsersWithPortfoliosCount(): number {
    return this.eligibleUsers.filter(u => u.portfolio_count > 0).length;
  }

  // Get total portfolio count across all users
  getTotalPortfolioCount(): number {
    return this.eligibleUsers.reduce((sum, u) => sum + u.portfolio_count, 0);
  }
}
